/**
 * A counting limiter, for bounding a fan-out where the work happens.
 *
 * WHY THIS EXISTS RATHER THAN A BUDGET AT ADMISSION. The expensive operations here — rasterising a PDF page,
 * cropping and OCR-ing a region — are reached from inside a handler, several calls deep, by code that fans out
 * over whatever the document happens to contain. A limit at the job level cannot see them: it admits one job and
 * that job then starts fifteen page conversions. So the limit belongs at the call site that allocates, where the
 * number is a property of the operation rather than a guess about the caller.
 *
 * SHARED ACROSS JOBS when the limiter is module-scoped, which is the point: two concurrent submission splits
 * should not each get their own allowance of page rasterisations. Create one per resource, not one per call.
 *
 * Deliberately tiny and dependency-free. `p-limit` would do the same thing; this is ten lines and avoids a
 * dependency in a path that runs in both the server bundle and the worker.
 */
export type Limiter = <T>(fn: () => Promise<T>) => Promise<T>;

export function createLimiter(max: number): Limiter {
  const ceiling = Math.max(1, Math.floor(max));
  let active = 0;
  const waiting: Array<() => void> = [];

  return async function run<T>(fn: () => Promise<T>): Promise<T> {
    // Acquire BEFORE incrementing, so a waiter that wakes up is the one that takes the slot the releaser freed.
    if (active >= ceiling) await new Promise<void>(resolve => waiting.push(resolve));
    active += 1;
    try {
      return await fn();
    } finally {
      active -= 1;
      // Hand the slot to the longest-waiting caller. `shift` rather than `pop` so a wide fan-out cannot starve
      // its own earliest items behind later ones.
      waiting.shift()?.();
    }
  };
}
